
import React from "react";
import { Brain, Zap, Target } from "lucide-react";

const AIXDRDifferenceSection: React.FC = () => {
  return (
    <div className="max-w-4xl mx-auto mb-16">
      <h2 className="text-3xl font-bold mb-6" id="ai-transformation">The AI Transformation: From Reactive to Predictive Leadership</h2>
      
      <p className="text-lg text-gray-700 mb-6">
        The integration of artificial intelligence into XDR platforms represents more than technological advancement - it's a fundamental shift in how security leaders think about threat detection, response, and organizational resilience. This transformation challenges us to move beyond traditional cybersecurity metrics toward strategic business outcomes.
      </p>
      
      <div className="bg-blue-50 p-6 rounded-lg border-l-4 border-blue-500 mb-8">
        <h3 className="text-xl font-semibold mb-3 text-blue-800">The Strategic Question</h3>
        <p className="text-blue-700">
          <strong>Instead of asking "How quickly can we detect threats?"</strong> AI-powered XDR enables leaders to ask: 
          <em>"How can we predict and prevent threats before they impact business operations?"</em>
        </p>
      </div>

      <h3 className="text-2xl font-semibold mb-6 text-gray-900">Three Pillars of AI-Driven Security Strategy</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
        <div className="bg-white p-8 rounded-lg shadow-md border border-slate-200 hover:shadow-lg transition-shadow">
          <div className="flex items-center mb-4">
            <Brain className="h-8 w-8 text-[#8932c8] mr-3" />
            <h4 className="font-bold text-xl">Intelligent Pattern Recognition</h4>
          </div>
          <p className="text-gray-700 mb-4">
            Advanced machine learning algorithms identify subtle behavioral patterns across vast datasets, enabling detection of sophisticated threats that evade traditional signature-based systems.
          </p>
          <div className="bg-purple-50 p-3 rounded text-sm">
            <strong>Strategic Impact:</strong> 85% reduction in false positives, allowing security teams to focus on genuine threats requiring human expertise.
          </div>
        </div>
        
        <div className="bg-white p-8 rounded-lg shadow-md border border-slate-200 hover:shadow-lg transition-shadow">
          <div className="flex items-center mb-4">
            <Zap className="h-8 w-8 text-[#8932c8] mr-3" />
            <h4 className="font-bold text-xl">Autonomous Response Orchestration</h4>
          </div>
          <p className="text-gray-700 mb-4">
            AI-driven automation executes complex response workflows across multiple security layers, containing threats at machine speed while preserving evidence for forensic analysis.
          </p>
          <div className="bg-yellow-50 p-3 rounded text-sm">
            <strong>Strategic Impact:</strong> Response times improved from hours to seconds, dramatically reducing potential breach impact and business disruption.
          </div>
        </div>
        
        <div className="bg-white p-8 rounded-lg shadow-md border border-slate-200 hover:shadow-lg transition-shadow">
          <div className="flex items-center mb-4">
            <Target className="h-8 w-8 text-[#8932c8] mr-3" />
            <h4 className="font-bold text-xl">Predictive Threat Intelligence</h4>
          </div>
          <p className="text-gray-700 mb-4">
            Historical analysis combined with real-time threat intelligence enables proactive security measures, shifting from reactive defense to predictive protection.
          </p>
          <div className="bg-green-50 p-3 rounded text-sm">
            <strong>Strategic Impact:</strong> Proactive threat prevention reduces successful attacks by 70%, transforming security from cost center to competitive advantage.
          </div>
        </div>
      </div>
      
      <div className="border-l-4 border-[#8932c8] pl-6 mb-8">
        <h3 className="text-xl font-semibold mb-3 text-gray-900">The Multiplier Effect</h3>
        <p className="text-lg text-gray-700 mb-4">
          The true power of AI-powered XDR emerges through the interaction of these capabilities. Machine learning identifies patterns, automation responds instantly, and predictive analytics anticipate future threats - creating a security posture that evolves faster than the threat landscape itself.
        </p>
        <p className="text-lg text-gray-700">
          Organizations implementing this integrated approach report transformational improvements: not just better security metrics, but fundamental changes in how they approach risk management, compliance, and strategic planning.
        </p>
      </div>

      <div className="bg-slate-50 p-6 rounded-lg mb-8">
        <h3 className="text-lg font-bold mb-3">Real-World Strategic Application</h3>
        <p className="text-gray-700 mb-3">
          Consider how a Fortune 500 financial services firm transformed their security operations: Instead of managing 50,000+ daily alerts with a team of 12 analysts, AI-powered XDR reduced actionable alerts to fewer than 50 high-confidence incidents, allowing the same team to focus on strategic threat hunting and business enablement.
        </p>
        <p className="text-gray-700">
          <strong>The result?</strong> Security became a business accelerator, enabling faster product launches and market expansion while actually improving risk posture.
        </p>
      </div>
    </div>
  );
};

export default AIXDRDifferenceSection;
